import React, { useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, UploadSimple, X, ArrowSquareOut, UserPlus, CopySimple } from "@phosphor-icons/react";
import RichStoryEditor from "@/components/RichStoryEditor";

const EMPTY = {
  title: "",
  slug: "",
  short_tag: "80G Tax Benefits",
  story: "",
  goal_amount: 500000,
  baseline_raised: 0,
  raised_override: null,
  images: [],
  story_images: [],
  qr_image: "",
  upi_id: "",
  bank_account_no: "",
  bank_account_name: "",
  bank_ifsc: "",
  preset_amounts: [
    { amount: 1500, label: "2 days food & medicines" },
    { amount: 3000, label: "Full treatment for 1 animal" },
    { amount: 5000, label: "Ambulance + treatment" },
    { amount: 9999, label: "Sponsor complete recovery" },
  ],
  min_amount: 300,
  is_active: true,
  allow_monthly: false,
  show_fake_donors: false,
  fake_donors: [],
  help_sections: [],
  faqs: [],
};

async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.url;
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-sm font-semibold mb-1.5">{label}</div>
      {children}
    </label>
  );
}

function CampaignForm({ value, onClose, onSaved }) {
  const [form, setForm] = useState(value);
  const [saving, setSaving] = useState(false);

  const patch = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addImage = async (file) => {
    try {
      const url = await uploadImage(file);
      patch("images", [...(form.images || []), url]);
      toast.success("Image uploaded");
    } catch (e) { toast.error("Upload failed"); }
  };

  const setQR = async (file) => {
    try { patch("qr_image", await uploadImage(file)); toast.success("QR uploaded"); }
    catch (e) { toast.error("Upload failed"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/admin/campaigns/${form.id}`, form);
        toast.success("Campaign updated");
      } else {
        await api.post("/admin/campaigns", form);
        toast.success("Campaign created");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 p-0 md:p-6 flex items-end md:items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-t-3xl md:rounded-3xl w-full max-w-4xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-[#DCE5D4] px-6 py-4 flex items-center justify-between z-10">
          <h3 className="font-display font-bold text-xl">{form.id ? "Edit" : "New"} Campaign</h3>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#EEF4E9]"><X size={20} /></button>
        </div>
        <div className="p-6 grid md:grid-cols-2 gap-5">
          <Field label="Title">
            <input value={form.title} onChange={(e) => patch("title", e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" data-testid="campaign-title-input" />
          </Field>
          <Field label="Slug (URL)">
            <input value={form.slug || ""} onChange={(e) => patch("slug", e.target.value)} placeholder="auto-generated if empty" className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" />
          </Field>
          <Field label="Tagline">
            <input value={form.short_tag || ""} onChange={(e) => patch("short_tag", e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" />
          </Field>
          <Field label="Goal amount (INR)">
            <input type="number" value={form.goal_amount} onChange={(e) => patch("goal_amount", Number(e.target.value))} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" />
          </Field>
          <Field label="Baseline raised (carry-forward)">
            <input type="number" value={form.baseline_raised} onChange={(e) => patch("baseline_raised", Number(e.target.value))} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" />
          </Field>
          <div>
            <Field label="Public raised amount (override)">
              <input
                type="number"
                value={form.raised_override ?? ""}
                onChange={(e) => patch("raised_override", e.target.value === "" ? null : Number(e.target.value))}
                placeholder="Leave blank to show the real total"
                className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white"
                data-testid="campaign-raised-override"
              />
            </Field>
            <div className="text-xs text-[#56624F] mt-1.5">
              If set, donors see this amount on the public site instead of the real raised total. The real total stays visible in the Campaigns table here.
            </div>
          </div>
          <Field label="Minimum donation (INR)">
            <input type="number" value={form.min_amount} onChange={(e) => patch("min_amount", Number(e.target.value))} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" />
          </Field>

          <div className="md:col-span-2">
            <Field label="Story">
              <RichStoryEditor value={form.story} onChange={(v) => patch("story", v)} />
              <div className="text-xs text-[#56624F] mt-1.5">Use the toolbar for bold, italic, highlight, bullet lists and text colours.</div>
            </Field>
          </div>

          <div className="md:col-span-2">
            <Field label="Campaign Images">
              <div className="flex items-center gap-2 flex-wrap">
                {(form.images || []).map((url, i) => (
                  <div key={i} className="relative w-24 h-24 rounded-xl overflow-hidden border border-[#DCE5D4]">
                    <img src={url} alt="" className="w-full h-full object-cover" />
                    <button
                      onClick={() => patch("images", form.images.filter((_, j) => j !== i))}
                      className="absolute top-1 right-1 bg-black/60 text-white w-6 h-6 rounded-full grid place-items-center"
                      type="button"
                    ><X size={12} /></button>
                  </div>
                ))}
                <label className="w-24 h-24 rounded-xl border-2 border-dashed border-[#DCE5D4] flex flex-col items-center justify-center cursor-pointer hover:border-[#4FAC39] text-[#56624F] text-xs" data-testid="upload-image-btn">
                  <UploadSimple size={18} />
                  <span>Upload</span>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && addImage(e.target.files[0])} />
                </label>
              </div>
            </Field>
          </div>

          <div className="md:col-span-2">
            <Field label="Our Story Photos" hint="Shown under the Our Story section. Leave empty to use defaults.">
              <div className="flex items-center gap-2 flex-wrap">
                {(form.story_images || []).map((url, i) => (
                  <div key={i} className="relative w-24 h-24 rounded-xl overflow-hidden border border-[#DCE5D4]">
                    <img src={url} alt="" className="w-full h-full object-cover" />
                    <button
                      onClick={() => patch("story_images", form.story_images.filter((_, j) => j !== i))}
                      className="absolute top-1 right-1 bg-black/60 text-white w-6 h-6 rounded-full grid place-items-center"
                      type="button"
                    ><X size={12} /></button>
                  </div>
                ))}
                <label className="w-24 h-24 rounded-xl border-2 border-dashed border-[#DCE5D4] flex flex-col items-center justify-center cursor-pointer hover:border-[#4FAC39] text-[#56624F] text-xs" data-testid="upload-story-image-btn">
                  <UploadSimple size={18} />
                  <span>Upload</span>
                  <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                    const f = e.target.files[0]; if (!f) return;
                    try { const url = await uploadImage(f); patch("story_images", [...(form.story_images || []), url]); toast.success("Story photo uploaded"); }
                    catch { toast.error("Upload failed"); }
                  }} />
                </label>
              </div>
            </Field>
          </div>

          <Field label="UPI ID">
            <input value={form.upi_id || ""} onChange={(e) => patch("upi_id", e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" />
          </Field>
          <Field label="UPI QR Image">
            <div className="flex items-center gap-3">
              {form.qr_image && <img src={form.qr_image} alt="QR" className="w-16 h-16 rounded-xl border border-[#DCE5D4]" />}
              <label className="btn-secondary cursor-pointer"><input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && setQR(e.target.files[0])} />Upload QR</label>
              {form.qr_image && <button type="button" onClick={() => patch("qr_image", "")} className="text-sm text-[#B84A34]">Remove</button>}
            </div>
          </Field>

          <Field label="Bank A/c Number">
            <input value={form.bank_account_no || ""} onChange={(e) => patch("bank_account_no", e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" />
          </Field>
          <Field label="Bank A/c Name">
            <input value={form.bank_account_name || ""} onChange={(e) => patch("bank_account_name", e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" />
          </Field>
          <Field label="IFSC">
            <input value={form.bank_ifsc || ""} onChange={(e) => patch("bank_ifsc", e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white" />
          </Field>

          <div className="md:col-span-2">
            <Field label="Preset amounts (JSON list of {amount,label})">
              <textarea rows={3} value={JSON.stringify(form.preset_amounts, null, 2)} onChange={(e) => { try { patch("preset_amounts", JSON.parse(e.target.value)); } catch {} }} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white font-mono text-xs" />
            </Field>
          </div>

          <div className="md:col-span-2">
            <Field label="Help sections (JSON)">
              <textarea rows={4} value={JSON.stringify(form.help_sections, null, 2)} onChange={(e) => { try { patch("help_sections", JSON.parse(e.target.value)); } catch {} }} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white font-mono text-xs" />
            </Field>
          </div>
          <div className="md:col-span-2">
            <Field label="FAQs (JSON list of {q,a})">
              <textarea rows={4} value={JSON.stringify(form.faqs, null, 2)} onChange={(e) => { try { patch("faqs", JSON.parse(e.target.value)); } catch {} }} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white font-mono text-xs" />
            </Field>
          </div>

          <label className="flex items-center gap-2 col-span-2">
            <input type="checkbox" checked={form.is_active} onChange={(e) => patch("is_active", e.target.checked)} data-testid="campaign-active-toggle" />
            Active (shown on public site)
          </label>
          <label className="flex items-center gap-2 col-span-2">
            <input type="checkbox" checked={!!form.allow_monthly} onChange={(e) => patch("allow_monthly", e.target.checked)} data-testid="campaign-monthly-toggle" />
            Allow monthly donations (auto-managed via Razorpay)
          </label>

          <div className="md:col-span-2 seva-card !bg-[#FBF9F6]">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div>
                <div className="font-display font-bold flex items-center gap-2"><UserPlus size={18} /> Fake Donors (for initial social proof)</div>
                <div className="text-xs text-[#56624F]">Shown under Top/Recent donors when enabled. Turn OFF once real donations start coming in.</div>
              </div>
              <label className="flex items-center gap-2 shrink-0">
                <input type="checkbox" checked={!!form.show_fake_donors} onChange={(e) => patch("show_fake_donors", e.target.checked)} data-testid="fake-donors-toggle" />
                <span className="text-sm font-semibold">{form.show_fake_donors ? "Showing" : "Hidden"}</span>
              </label>
            </div>
            {form.show_fake_donors && (
              <div className="space-y-2">
                {(form.fake_donors || []).map((f, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`fake-donor-row-${i}`}>
                    <input value={f.name || ""} onChange={(e) => { const arr = [...form.fake_donors]; arr[i] = { ...f, name: e.target.value }; patch("fake_donors", arr); }} placeholder="Donor name" className="col-span-5 px-3 py-2 rounded-lg border border-[#DCE5D4] bg-white text-sm" />
                    <input type="number" value={f.amount || ""} onChange={(e) => { const arr = [...form.fake_donors]; arr[i] = { ...f, amount: Number(e.target.value) }; patch("fake_donors", arr); }} placeholder="Amount" className="col-span-3 px-3 py-2 rounded-lg border border-[#DCE5D4] bg-white text-sm" />
                    <input type="number" value={f.days_ago ?? ""} onChange={(e) => { const arr = [...form.fake_donors]; arr[i] = { ...f, days_ago: Number(e.target.value) }; patch("fake_donors", arr); }} placeholder="Days ago" className="col-span-3 px-3 py-2 rounded-lg border border-[#DCE5D4] bg-white text-sm" />
                    <button type="button" onClick={() => patch("fake_donors", form.fake_donors.filter((_, j) => j !== i))} className="col-span-1 text-[#B84A34] hover:bg-[#FDEAE5] rounded-lg p-2"><Trash size={16} /></button>
                  </div>
                ))}
                <button type="button" onClick={() => patch("fake_donors", [...(form.fake_donors || []), { name: "", amount: 1500, days_ago: 1 }])} className="btn-secondary !py-2 !px-3 text-sm" data-testid="add-fake-donor-btn">
                  <Plus size={14} className="inline mr-1" /> Add donor
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-[#DCE5D4] px-6 py-4 flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary" data-testid="save-campaign-btn">{saving ? "Saving..." : "Save Campaign"}</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminCampaigns() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);

  const refresh = () => api.get("/admin/campaigns").then((r) => setList(r.data || []));
  useEffect(() => { refresh(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this campaign? This cannot be undone.")) return;
    await api.delete(`/admin/campaigns/${id}`);
    toast.success("Deleted");
    refresh();
  };

  const duplicate = async (id) => {
    try {
      const { data } = await api.post(`/admin/campaigns/${id}/duplicate`);
      toast.success("Campaign duplicated as a draft");
      refresh();
      setEditing(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Duplicate failed");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-bold text-3xl md:text-4xl">Campaigns</h1>
          <p className="text-[#56624F] mt-1">Create and manage donation campaigns</p>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} className="btn-primary inline-flex items-center gap-1.5" data-testid="new-campaign-btn">
          <Plus size={16} weight="bold" /> New
        </button>
      </div>

      <div className="seva-card !p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#EEF4E9]">
              <tr className="text-left text-[#56624F]">
                <th className="py-3 px-4">Title</th>
                <th className="py-3 px-4">Goal</th>
                <th className="py-3 px-4">Raised</th>
                <th className="py-3 px-4">Donors</th>
                <th className="py-3 px-4">Slug</th>
                <th className="py-3 px-4">Active</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id} className="border-t border-[#DCE5D4]" data-testid={`campaign-row-${c.slug}`}>
                  <td className="py-3 px-4 font-medium max-w-xs truncate">{c.title}</td>
                  <td className="py-3 px-4">{formatINR(c.goal_amount)}</td>
                  <td className="py-3 px-4">
                    <div className="font-semibold">{formatINR(c.raised)}</div>
                    {c.raised_override != null && (
                      <div className="text-xs text-[#4FAC39] mt-0.5" data-testid={`campaign-raised-override-badge-${c.slug}`}>
                        Public shows {formatINR(c.raised_override)}
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-4">{c.donor_count}</td>
                  <td className="py-3 px-4 text-[#56624F]"><a target="_blank" rel="noreferrer" href={`${process.env.PUBLIC_URL}/campaigns/${c.slug}`} className="inline-flex items-center gap-1 hover:text-[#4FAC39]">/{c.slug} <ArrowSquareOut size={12} /></a></td>
                  <td className="py-3 px-4">
                    <span className={"text-xs px-2 py-1 rounded-full " + (c.is_active ? "bg-[#E6F3DC] text-[#2F7A4A]" : "bg-[#EEF4E9] text-[#56624F]")}>
                      {c.is_active ? "Live" : "Draft"}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right space-x-2">
                    <button onClick={() => setEditing(c)} className="p-1.5 rounded-lg hover:bg-[#EEF4E9]" data-testid={`edit-campaign-${c.slug}`}><PencilSimple size={16} /></button>
                    <button onClick={() => duplicate(c.id)} className="p-1.5 rounded-lg hover:bg-[#EEF4E9]" title="Duplicate" data-testid={`duplicate-campaign-${c.slug}`}><CopySimple size={16} /></button>
                    <button onClick={() => del(c.id)} className="p-1.5 rounded-lg hover:bg-[#EEF4E9] text-[#B84A34]"><Trash size={16} /></button>
                  </td>
                </tr>
              ))}
              {!list.length && <tr><td colSpan={7} className="py-10 text-center text-[#56624F]">No campaigns yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {editing && <CampaignForm value={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />}
    </div>
  );
}
